import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { trackEvent } from "src/trackEvent";
import type { RagAgent } from "../api/ragApi";
import type { AgentFormData } from "../components/AgentConfigFields";
import { createAgentThunk, updateAgentThunk } from "../state/ragSlice";

const errorMessageOf = (err: unknown) =>
  typeof err === "string"
    ? err
    : typeof err === "object" && err !== null && "message" in err && typeof err.message === "string"
    ? err.message
    : "";

export const useAgentFormSubmit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false);

  const handleError = (err: unknown) => {
    if (errorMessageOf(err).includes("Agent limit")) {
      dispatch(setAlert({ type: "danger", msg: t("pages.agents.quotaAgentsExceeded") }));
    } else {
      console.error("Failed to save agent:", err);
    }
  };

  const createAgent = async (data: AgentFormData): Promise<RagAgent | null> => {
    setSubmitting(true);
    try {
      trackEvent("agent-create");
      const agent = await dispatch(createAgentThunk(data)).unwrap();
      dispatch(setAlert({ type: "success", msg: t("pages.agents.agentCreated") }));
      return agent;
    } catch (err) {
      handleError(err);
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const updateAgent = async (id: number, data: AgentFormData): Promise<boolean> => {
    setSubmitting(true);
    try {
      await dispatch(updateAgentThunk({ id, data })).unwrap();
      dispatch(setAlert({ type: "success", msg: t("pages.agents.agentSaved") }));
      return true;
    } catch (err) {
      handleError(err);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, createAgent, updateAgent };
};
